import { useCallback, useEffect, useState } from "react";
import { Plus, Loader2, KeyRound, ShieldCheck, Pencil, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { PageHeader } from "@/pages/admin/AdminLayout";
import { AppAccessQr } from "@/components/AppAccessQr";
import { SecureGate } from "@/components/SecureGate";
import { AdminPowerCard } from "@/components/AdminPowerCard";
import { useAuth } from "@/lib/auth";
import { downloadBlob } from "@/lib/pdf";
import { TechScheduleDialog, scheduleSummary } from "@/components/TechScheduleDialog";
import { CalendarClock } from "lucide-react";

const EMPTY = { name: "", email: "", phone: "", password: "", employment: "dipendente", geo_enabled: true, push_required: true, permissions: ["jobs_view", "absences"] };
const PERMS = [["jobs_view", "Vedere interventi"], ["jobs_edit", "Creare e modificare interventi"], ["cantieri", "Gestire cantieri"],
  ["absences", "Approvare permessi"], ["punches", "Timbrature"], ["leads", "Contatti"], ["whatsapp", "WhatsApp"]];
const PRESET = { dipendente: { geo_enabled: true, push_required: true }, autonomo: { geo_enabled: false, push_required: true } };

function TechDialog({ open, onOpenChange, onSaved, role, editing }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setF(editing ? { name: editing.name, email: editing.email, phone: editing.phone || "", password: "", employment: editing.employment || "dipendente", geo_enabled: editing.geo_enabled !== false, push_required: editing.push_required !== false, permissions: editing.permissions || [] } : EMPTY);
  }, [open, editing]);
  const upd = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const save = async () => {
    setBusy(true);
    try {
      if (editing) {
        await api.patch(`/admin/technicians/${editing.id}`, { name: f.name.trim(), email: f.email.trim(), phone: f.phone.trim() || null, employment: f.employment, geo_enabled: f.geo_enabled, push_required: f.push_required, ...(role === "responsabile" ? { permissions: f.permissions } : {}) });
        toast.success("Dati aggiornati");
      } else {
        await api.post("/admin/technicians", { ...f, role, phone: f.phone || null });
        toast.success(role === "responsabile" ? "Responsabile creato" : "Tecnico creato");
      }
      setF(EMPTY);
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="tech-create-dialog">
        <DialogHeader><DialogTitle className="font-display">{editing ? `Modifica ${editing.name}` : role === "responsabile" ? "Nuovo responsabile" : "Nuovo tecnico"}</DialogTitle></DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2 sm:col-span-2"><Label>Nome e cognome</Label><Input value={f.name} onChange={upd("name")} data-testid="tech-name-input" /></div>
          <div className="space-y-2"><Label>Email (login)</Label><Input type="email" value={f.email} onChange={upd("email")} data-testid="tech-email-input" /></div>
          <div className="space-y-2"><Label>Telefono</Label><Input value={f.phone} onChange={upd("phone")} data-testid="tech-phone-input" /></div>
          {!editing && <div className="space-y-2"><Label>Password iniziale</Label><Input type="text" value={f.password} onChange={upd("password")} data-testid="tech-password-input" /></div>}
          {role === "tecnico" && (
            <div className="space-y-3 rounded-xl border p-3" data-testid="tech-permissions">
              <div className="grid grid-cols-2 gap-2">
                {[["dipendente", "Dipendente"], ["autonomo", "Autonomo"]].map(([v, l]) => (
                  <button key={v} type="button" data-active={f.employment === v} onClick={() => setF((p) => ({ ...p, employment: v, ...PRESET[v] }))}
                    className="choice rounded-full border bg-white py-1.5 text-sm" data-testid={`tech-employment-${v}`}>{l}</button>
                ))}
              </div>
              <p className="text-[11px] text-slate-500">{f.employment === "autonomo" ? "Riceve solo gli interventi assegnati, deve accettarli dall'app e indica le sue disponibilità. Niente timbrature." : "Timbratura con QR, geolocalizzazione in orario di lavoro e notifiche."}</p>
              <label className="flex items-center justify-between text-sm">Geolocalizzazione<Switch checked={f.geo_enabled} onCheckedChange={(v) => setF((p) => ({ ...p, geo_enabled: v }))} data-testid="tech-geo-switch" /></label>
              <label className="flex items-center justify-between text-sm">Notifiche obbligatorie<Switch checked={f.employment === "autonomo" || f.push_required} disabled={f.employment === "autonomo"} onCheckedChange={(v) => setF((p) => ({ ...p, push_required: v }))} data-testid="tech-push-switch" /></label>
              {f.employment === "autonomo" && <p className="text-[11px] text-slate-500" data-testid="tech-push-locked-note">Per gli autonomi le notifiche push sono sempre obbligatorie.</p>}
            </div>
          )}
          {role === "responsabile" && (
            <div className="space-y-2 rounded-xl border p-3 sm:col-span-2" data-testid="manager-permissions">
              <p className="text-sm font-medium">Permessi nell'area ufficio</p>
              <div className="grid gap-2 sm:grid-cols-2">
                {PERMS.map(([k, l]) => (
                  <label key={k} className="flex items-center gap-2 text-sm">
                    <Checkbox checked={f.permissions.includes(k)} onCheckedChange={(v) => setF((p) => ({ ...p, permissions: v ? [...p.permissions, k] : p.permissions.filter((x) => x !== k) }))} data-testid={`perm-${k}`} />{l}
                  </label>
                ))}
              </div>
              <p className="text-[11px] text-slate-500">Impostazioni, utenti e percorsi restano solo all'amministratore. Il responsabile timbra con il QR e può essere assegnato ai cantieri.</p>
              <label className="flex items-center justify-between text-sm">Geolocalizzazione in orario di lavoro<Switch checked={f.geo_enabled} onCheckedChange={(v) => setF((p) => ({ ...p, geo_enabled: v }))} data-testid="manager-geo-switch" /></label>
            </div>
          )}
        </div>
        {editing && <p className="text-xs text-slate-500">Se cambi l'email, il dipendente dovrà usare la nuova email per accedere. La password si cambia con "Reimposta password".</p>}
        <DialogFooter><Button onClick={save} disabled={busy} className="rounded-full" data-testid="tech-save-button">{busy && <Loader2 className="h-4 w-4 animate-spin" />} {editing ? "Salva modifiche" : "Crea account"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const fmtDay = (iso) => new Date(iso).toLocaleDateString("it-IT");

function PrivacyRow({ t, i, st, onSent }) {
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/privacy/send/${t.id}`);
      if (data.wa_status === "queued") toast.success("Informativa inviata su WhatsApp");
      else {
        toast.warning("Invio automatico non riuscito: si apre WhatsApp per inviarla a mano");
        window.open(data.manual_link, "_blank");
      }
      onSent();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const pdf = async () => {
    try {
      const { data } = await api.get(`/admin/privacy/pdf/${t.id}`, { responseType: "blob" });
      downloadBlob(data, `informativa-${t.name.replace(/\W+/g, "-")}.pdf`);
    } catch {
      toast.error("Download non riuscito");
    }
  };
  return (
    <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t pt-3 text-xs" data-testid={`tech-privacy-${i}`}>
      <span data-testid={`tech-privacy-status-${i}`}>Informativa: {st?.accepted
        ? <b className="text-[#3f6d18]">confermata il {fmtDay(st.accepted_at)}</b>
        : <b className="text-amber-700">{st?.old_version ? "da riconfermare" : "non confermata"}{st?.sent_at ? ` (inviata il ${fmtDay(st.sent_at)})` : ""}</b>}</span>
      <div className="flex gap-1.5">
        {st?.has_pdf && (
          <Button size="sm" variant="outline" className="h-7 rounded-full px-2.5 text-xs" onClick={pdf} data-testid={`tech-privacy-pdf-${i}`}>
            <FileDown className="h-3 w-3" /> PDF
          </Button>
        )}
        <Button size="sm" variant="outline" className="h-7 rounded-full px-2.5 text-xs" disabled={busy} onClick={send} data-testid={`tech-privacy-send-${i}`}>
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <ShieldCheck className="h-3 w-3" />} Invia informativa
        </Button>
      </div>
    </div>
  );
}

export default function Technicians() {
  const { user: me } = useAuth();
  const [techs, setTechs] = useState([]);
  const [editing, setEditing] = useState(null);
  const [sched, setSched] = useState(null);
  const [privacy, setPrivacy] = useState({});
  const [avail, setAvail] = useState({});
  useEffect(() => { api.get("/admin/availability").then((r) => setAvail(r.data)).catch(() => {}); }, []);
  const loadPrivacy = useCallback(() => api.get("/admin/privacy/status").then((r) => setPrivacy(r.data)), []);
  useEffect(() => { loadPrivacy(); }, [loadPrivacy]);
  const [open, setOpen] = useState(false);
  const [role, setRole] = useState("tecnico");
  const load = useCallback(() => api.get("/admin/technicians", { params: { role } }).then((r) => setTechs(r.data)), [role]);
  useEffect(() => { load(); }, [load]);

  const patch = async (t, body, msg) => {
    try {
      await api.patch(`/admin/technicians/${t.id}`, body);
      toast.success(msg);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const resetPwd = (t) => {
    const p = window.prompt(`Nuova password per ${t.name} (min 6 caratteri)`);
    if (p) patch(t, { password: p }, "Password aggiornata");
  };

  return (
    <div>
      <PageHeader eyebrow="Squadra" title="Utenti">
        <AppAccessQr />
        <Button onClick={() => setOpen(true)} className="rounded-full" data-testid="tech-add-button"><Plus className="h-4 w-4" /> {role === "responsabile" ? "Nuovo responsabile" : "Nuovo tecnico"}</Button>
      </PageHeader>
      {me?.role === "admin" && <div className="mb-5"><SecureGate><AdminPowerCard onChange={load} /></SecureGate></div>}
      <Tabs value={role} onValueChange={setRole} className="mb-5">
        <TabsList>
          <TabsTrigger value="tecnico" data-testid="users-tab-tecnici">Tecnici</TabsTrigger>
          <TabsTrigger value="responsabile" data-testid="users-tab-responsabili">Responsabili</TabsTrigger>
        </TabsList>
      </Tabs>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="tech-list">
        {techs.map((t, i) => (
          <div key={t.id} className="rounded-2xl border bg-white p-5" data-testid={`tech-card-${i}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <span className="grid h-11 w-11 place-items-center rounded-full bg-[#0f1710] font-display font-bold text-[#a2ca6b]">
                  {t.name.split(" ").map((x) => x[0]).slice(0, 2).join("")}
                </span>
                <div><p className="font-semibold">{t.name}{t.admin_power && <span className="ml-2 rounded-full bg-[#0f1710] px-2 py-0.5 text-[10px] font-bold text-[#a2ca6b]" data-testid={`tech-admin-badge-${i}`}>ADMIN</span>}</p><p className="text-xs text-slate-500">{t.email}</p></div>
              </div>
              <Switch checked={t.active} onCheckedChange={(v) => patch(t, { active: v }, v ? "Account attivato" : "Account disattivato")} data-testid={`tech-active-switch-${i}`} />
            </div>
            <div className="mt-4 space-y-1 text-sm text-slate-600">
              <p>Tel: {t.phone || "—"}</p>
              {role === "tecnico" && <p className="truncate" data-testid={`tech-calendar-status-${i}`}>Google Calendar: {t.calendar_connected
                ? <span className="font-semibold text-[#3f6d18]">collegato ({t.google_email})</span>
                : <span className="font-semibold text-amber-700">non collegato</span>}</p>}
            </div>
            <div className="mt-3 -ml-2 flex flex-wrap">
              <Button variant="ghost" size="sm" onClick={() => resetPwd(t)} data-testid={`tech-reset-password-${i}`}><KeyRound className="h-4 w-4" /> Reimposta password</Button>
              <Button variant="ghost" size="sm" onClick={() => setEditing(t)} data-testid={`tech-edit-${i}`}><Pencil className="h-4 w-4" /> Modifica dati</Button>
              <Button variant="ghost" size="sm" onClick={() => setSched(t)} data-testid={`tech-schedule-${i}`}><CalendarClock className="h-4 w-4" /> Orario interventi</Button>
            </div>
            <PrivacyRow t={t} i={i} st={privacy[t.id]} onSent={loadPrivacy} />
            {t.role === "tecnico" && (
              <p className="mt-2 text-xs text-slate-500" data-testid={`tech-employment-label-${i}`}>
                <b>{t.employment === "autonomo" ? "Autonomo" : "Dipendente"}</b> · posizione {t.geo_enabled === false ? "off" : "on"} · notifiche {t.push_required === false ? "facoltative" : "obbligatorie"}
                {t.employment === "autonomo" && ` · disponibile: ${(avail[t.id] || []).slice(0, 6).map((d) => d.slice(8, 10) + "/" + d.slice(5, 7)).join(", ") || "nessuna data"}`}
                <span className="block" data-testid={`tech-schedule-summary-${i}`}>{scheduleSummary(t.work_schedule)}</span>
              </p>
            )}
          </div>
        ))}
        {!techs.length && <p className="text-sm text-slate-500" data-testid="tech-empty">Nessun account ancora.</p>}
      </div>
      <TechScheduleDialog tech={sched} onClose={() => setSched(null)} onSaved={() => { setSched(null); load(); }} />
      <TechDialog open={open} onOpenChange={setOpen} onSaved={load} role={role} />
      <TechDialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)} onSaved={() => { load(); loadPrivacy(); }} role={role} editing={editing} />
    </div>
  );
}
